/**
 * AnalogClock — Prompt 18B2-P6 §3 · §6 · §36.
 *
 * Small pure-SVG analog clock. Real current local time. Hour and
 * minute hands update every 30 seconds; second hand is intentionally
 * omitted so the entire component is visually calm and does not
 * pull the Employee Mobile tree into a per-second rerender.
 *
 *   * Center label renders the caller-provided `centerLabel`
 *     (e.g. the current restaurant name) — decorative, safe-fallback
 *     to `"Restaurant"` when nothing is provided.
 *   * The component is decorative; the readable time lives in
 *     the sibling `DigitalTime` element (§7 · §31).
 */
import { useEffect, useState } from "react";

const HOUR_MARKS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

// Prompt 18B2-P6A §2 · §4 — a small shared live-time hook, exported
// so `AnalogClock` and `DigitalTime` render the SAME source of
// truth. Ticking is isolated inside consuming components; the rest
// of the Employee Mobile tree (Staff Meal, Clock In/Out state,
// Schedule, auth) is NEVER rerendered by the tick.
export function useLiveNow(intervalMs = 15_000) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
}

export function AnalogClock({
    centerLabel = "",
    size = 168,
    now,
}) {
    // Prompt 18B2-P6A §3 — accept the current-time value from the
    // shared live-time source. Fall back to the local hook when no
    // `now` is supplied so callers that use `AnalogClock` alone
    // still tick.
    const _liveNow = useLiveNow(30_000);
    const _now = now instanceof Date ? now : _liveNow;

    const hours = _now.getHours() % 12;
    const minutes = _now.getMinutes();
    // Continuous hour hand so it inches between marks between minutes.
    const hourAngle = (hours + minutes / 60) * 30;
    const minuteAngle = minutes * 6;

    const cx = size / 2;
    const cy = size / 2;
    const rDial = size * 0.46;
    const rMinuteTick = size * 0.44;
    const rHourNumber = size * 0.38;

    // Prompt 18B2-P9 §6 · §7 — the center label is decorative only.
    // Never render the historical "Restaurant" placeholder — it
    // added visual noise inside the dial without communicating
    // anything meaningful (§7 · §13).
    const _renderCenterLabel = Boolean(
        centerLabel
        && centerLabel !== "Restaurant"
        && centerLabel !== "RESTAURANT");

    return (
        <svg
            data-testid="mobile-analog-clock"
            role="img"
            aria-label={`Analog clock showing ${
                String(_now.getHours()).padStart(2, "0")
            }:${String(_now.getMinutes()).padStart(2, "0")}`}
            viewBox={`0 0 ${size} ${size}`}
            width={size}
            height={size}
            style={{ maxWidth: "100%", height: "auto" }}
        >
            {/* Prompt 18B2-P9 §6 — thinner watch-like outer stroke.
                The heavy 1.25px `#1a1a1a` ring of P6 read as a
                dashboard element; the refined dial uses a light
                neutral hairline so the clock reads as a native
                watch face. */}
            <circle
                cx={cx}
                cy={cy}
                r={rDial}
                fill="none"
                stroke="#d4d4d4"
                strokeWidth={0.75}
            />
            {/* Minute ticks — restrained. Only the four cardinal
                (12/3/6/9) marks and the eight quarter positions
                render; the fine 60-tick ring is dropped so the
                dial breathes. */}
            {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i * 30) * (Math.PI / 180);
                const x1 = cx + Math.sin(angle) * rMinuteTick;
                const y1 = cy - Math.cos(angle) * rMinuteTick;
                const isCardinal = i % 3 === 0;
                const inner = isCardinal
                    ? rMinuteTick - size * 0.05
                    : rMinuteTick - size * 0.025;
                const x2 = cx + Math.sin(angle) * inner;
                const y2 = cy - Math.cos(angle) * inner;
                return (
                    <line
                        key={`tick-${i}`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={isCardinal ? "#525252" : "#a3a3a3"}
                        strokeWidth={isCardinal ? 1 : 0.5}
                        strokeLinecap="round"
                    />
                );
            })}
            {/* Hour numbers */}
            {HOUR_MARKS.map((n, idx) => {
                const angle = (idx * 30) * (Math.PI / 180);
                const x = cx + Math.sin(angle) * rHourNumber;
                const y = cy - Math.cos(angle) * rHourNumber;
                return (
                    <text
                        key={`hn-${n}`}
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={size * 0.08}
                        fontFamily={
                            "-apple-system, BlinkMacSystemFont, "
                            + "'Segoe UI', system-ui, sans-serif"}
                        fontWeight={500}
                        fill="#404040"
                    >
                        {n}
                    </text>
                );
            })}
            {_renderCenterLabel && (
                <text
                    data-testid="mobile-analog-clock-center-label"
                    x={cx}
                    y={cy - size * 0.08}
                    textAnchor="middle"
                    fontSize={size * 0.05}
                    fontFamily={
                        "-apple-system, BlinkMacSystemFont, "
                        + "'Segoe UI', system-ui, sans-serif"}
                    fill="#737373"
                    letterSpacing={1.5}
                    style={{ textTransform: "uppercase" }}
                >
                    {centerLabel}
                </text>
            )}
            {/* Hour hand — refined thin native-watch stroke. */}
            <line
                data-testid="mobile-analog-clock-hour-hand"
                x1={cx}
                y1={cy}
                x2={cx + Math.sin(hourAngle * Math.PI / 180) * (size * 0.26)}
                y2={cy - Math.cos(hourAngle * Math.PI / 180) * (size * 0.26)}
                stroke="#0a0a0a"
                strokeWidth={2.5}
                strokeLinecap="round"
            />
            {/* Minute hand */}
            <line
                data-testid="mobile-analog-clock-minute-hand"
                x1={cx}
                y1={cy}
                x2={cx + Math.sin(minuteAngle * Math.PI / 180) * (size * 0.36)}
                y2={cy - Math.cos(minuteAngle * Math.PI / 180) * (size * 0.36)}
                stroke="#0a0a0a"
                strokeWidth={1.75}
                strokeLinecap="round"
            />
            {/* Center cap */}
            <circle cx={cx} cy={cy} r={size * 0.018} fill="#0a0a0a" />
        </svg>
    );
}

export default AnalogClock;
